import { useRef, useState, useCallback } from "react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";

interface LuxuryCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  href?: string;
  className?: string;
}

const MAX_TILT = 5;

export function LuxuryCard({
  icon,
  title,
  description,
  features,
  href,
  className,
}: LuxuryCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState({ rotateX: 0, rotateY: 0 });
  const [glowPos, setGlowPos] = useState<{ x: number; y: number } | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = cardRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      setTransform({
        rotateX: -y * MAX_TILT,
        rotateY: x * MAX_TILT,
      });
      setGlowPos({ x: (e.clientX - rect.left) / rect.width * 100, y: (e.clientY - rect.top) / rect.height * 100 });
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    setTransform({ rotateX: 0, rotateY: 0 });
    setGlowPos(null);
  }, []);

  const content = (
    <>
      <div
        className={cn(
          "absolute inset-0 rounded-2xl transition-opacity duration-500",
          glowPos ? "opacity-100" : "opacity-0"
        )}
        style={
          glowPos
            ? {
                background: `radial-gradient(circle at ${glowPos.x}% ${glowPos.y}%, hsl(var(--primary) / 0.08), transparent 60%)`,
              }
            : undefined
        }
      />
      <div className="relative z-10 w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110 group-hover:rotate-[3deg] group-hover:text-primary text-primary/80">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2 font-serif">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4">{description}</p>
      <ul className="space-y-2">
        {features.map((f, i) => (
          <li key={i} className="text-xs text-muted-foreground/90 flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-primary/50 shrink-0" />
            {f}
          </li>
        ))}
      </ul>
      <span
        className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent w-0 group-hover:w-full transition-all duration-700"
        aria-hidden
      />
    </>
  );

  const cardClassName = cn(
    "group relative bg-card/60 backdrop-blur-md border border-border/30 rounded-2xl overflow-hidden",
    "transition-all duration-500 ease-out",
    "hover:shadow-[0_20px_60px_-15px_rgba(235,192,83,0.15)] hover:border-primary/30 hover:-translate-y-2",
    "pb-8 pt-7 px-6",
    className
  );

  const style = {
    transform: `perspective(1000px) rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg)`,
  };

  if (href) {
    return (
      <Link href={href}>
        <div
          ref={cardRef}
          className={cardClassName}
          style={style}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {content}
        </div>
      </Link>
    );
  }

  return (
    <div
      ref={cardRef}
      className={cardClassName}
      style={style}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {content}
    </div>
  );
}
